import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth as authAPI, scratchCards } from '../services/api';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import app from '../firebase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionType, setSessionType] = useState(null); // 'admin' or 'user'
  const auth = getAuth(app);

  // Session management functions
  const switchToAdminSession = () => {
    const adminData = localStorage.getItem('admin');
    if (adminData) {
      setAdmin(JSON.parse(adminData));
      setUser(null);
      setSessionType('admin');
    }
  };

  const switchToUserSession = () => {
    const userData = sessionStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
      setAdmin(null);
      setSessionType('user');
    }
  };

  const saveUserSession = (userData) => {
    sessionStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setAdmin(null);
    setSessionType('user');
  };

  const saveAdminSession = (adminData) => {
    console.log('Saving admin session:', adminData);
    localStorage.setItem('admin', JSON.stringify(adminData));
    setAdmin(adminData);
    setUser(null);
    setSessionType('admin');
    console.log('Admin session saved successfully');
  };

  // Initialize session on mount
  useEffect(() => {
    const initializeSession = () => {
      const adminData = localStorage.getItem('admin');
      const userData = sessionStorage.getItem('user');
      
      console.log('Initializing session - adminData:', adminData, 'userData:', userData);
      
      if (adminData) {
        const parsedAdminData = JSON.parse(adminData);
        console.log('Setting admin from localStorage:', parsedAdminData);
        setAdmin(parsedAdminData);
        setSessionType('admin');
      } else if (userData) {
        const parsedUserData = JSON.parse(userData);
        console.log('Setting user from sessionStorage:', parsedUserData);
        setUser(parsedUserData);
        setSessionType('user');
      }
      setLoading(false);
    };

    initializeSession();
  }, []);

  useEffect(() => {
    // Listen for Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('Firebase auth state changed:', firebaseUser);
      if (firebaseUser) {
        try {
          const idToken = await firebaseUser.getIdToken();
          const adminData = localStorage.getItem('admin');
          
          if (adminData) {
            // Admin user
            console.log('Setting admin user:', JSON.parse(adminData));
            saveAdminSession(JSON.parse(adminData));
          } else {
            // Customer user
            const userData = {
              uid: firebaseUser.uid,
              phoneNumber: firebaseUser.phoneNumber,
              displayName: firebaseUser.displayName,
              loyaltyPoints: 0 // Will be fetched from server
            };
            console.log('Setting customer user:', userData);
            saveUserSession(userData);
            
            // Fetch user points from server with a small delay to ensure auth is ready
            setTimeout(async () => {
              try {
                const pointsResponse = await scratchCards.getUserPoints();
                if (pointsResponse.data) {
                  console.log('Fetched user points:', pointsResponse.data);
                  const updatedUserData = {
                    ...userData,
                    loyaltyPoints: pointsResponse.data.currentPoints || 0
                  };
                  saveUserSession(updatedUserData);
                }
              } catch (error) {
                console.warn('Could not fetch user points:', error);
                // Keep user logged in even if points fetch fails
              }
            }, 1000);
          }
        } catch (error) {
          console.error('Error getting ID token:', error);
        }
      } else {
        console.log('No Firebase user, clearing auth state');
        setAdmin(null);
        setUser(null);
        setSessionType(null);
        localStorage.removeItem('admin');
        sessionStorage.removeItem('user');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  const login = async (user, isAdmin = false) => {
    try {
      if (isAdmin) {
        const adminData = {
          uid: user.uid,
          email: user.email,
          isAdmin: true,
          name: user.displayName || 'Admin User'
        };
        saveAdminSession(adminData);
      } else {
        const userData = {
          uid: user.uid,
          phoneNumber: user.phoneNumber,
          displayName: user.displayName,
          loyaltyPoints: 0
        };
        saveUserSession(userData);
      }
      return true;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async (sessionType = null) => {
    try {
      console.log('Logout called with sessionType:', sessionType);
      
      if (sessionType === 'admin' || sessionType === null) {
        console.log('Removing admin session');
        localStorage.removeItem('admin');
        setAdmin(null);
      }
      if (sessionType === 'user' || sessionType === null) {
        console.log('Removing user session');
        sessionStorage.removeItem('user');
        setUser(null);
      }
      
      if (sessionType === null) {
        // Logout from Firebase only if logging out completely
        console.log('Signing out from Firebase');
        await auth.signOut();
        setSessionType(null);
      } else {
        // For specific session logout, just clear the session type
        setSessionType(null);
      }
      
      console.log('Logout completed');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const sendOtp = async (phone) => {
    return await authAPI.sendOtp(phone);
  };

  const verifyOtp = async (phone, otp) => {
    return await authAPI.verifyOtp(phone, otp);
  };

  const updateUserPoints = (points) => {
    if (user) {
      setUser(prev => ({ ...prev, loyaltyPoints: points }));
    }
  };

  return (
    <AuthContext.Provider value={{ 
      admin, 
      user, 
      sessionType,
      login, 
      logout, 
      loading, 
      sendOtp, 
      verifyOtp, 
      updateUserPoints,
      switchToAdminSession,
      switchToUserSession
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};